import { useState, type FormEvent } from 'react';
import { CalendarDays, Clock, Hash, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { BornesPointage } from './BornesPointage';
import { PresenceJour } from './PresenceJour';
import { AbsencesPointage } from './AbsencesPointage';
import { EtatMensuelPointage } from './EtatMensuelPointage';
import { ReglesPaiePointage } from './ReglesPaiePointage';
import { pointageApi, type EmployePointage, type HorairePointage, type NouvelHoraire } from '@/api/pointage';

/**
 * Pointage des présences — P1 : paramètres (docs/PLAN-POINTAGE.md).
 * Horaires datés (un nouvel horaire ne réécrit pas le passé), jours fériés, numéro de pointage de chaque employé.
 * P2 : bornes de pointage. P3 : onglets Présence du jour et Absences (suivi RH).
 */

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const CLE = ['hr', 'pointage'] as const;

const joursLisibles = (j: number[]) => {
  const tries = [...j].sort((a, b) => a - b);
  const suite = tries.every((n, i) => i === 0 || n === tries[i - 1] + 1);
  return suite && tries.length > 2
    ? `du ${JOURS[tries[0] - 1]} au ${JOURS[tries[tries.length - 1] - 1]}`
    : tries.map((n) => JOURS[n - 1]).join(', ');
};
const dateFr = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC' });
const aujourdhui = () => new Date().toISOString().slice(0, 10);

export const PointagePage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const peutHoraire = role === 'daf' || role === 'admin_holding';
  const peutSaisir  = peutHoraire || role === 'rh';
  const qc = useQueryClient();
  const annee = new Date().getFullYear();
  const [anneeFeries, setAnneeFeries] = useState(annee);

  const horaires = useQuery({ queryKey: [...CLE, 'horaires'], queryFn: pointageApi.horaires });
  const feries   = useQuery({ queryKey: [...CLE, 'feries', anneeFeries], queryFn: () => pointageApi.joursFeries(anneeFeries) });
  const employes = useQuery({ queryKey: [...CLE, 'employes'], queryFn: pointageApi.employes });

  const enVigueur = horaires.data?.find((h) => h.enVigueur) ?? null;
  const aVenir    = horaires.data?.filter((h) => h.valableDu > aujourdhui()) ?? [];
  const [formHoraire, setFormHoraire] = useState(false);
  const [onglet, setOnglet] = useState<'presence' | 'absences' | 'etat' | 'parametres'>('presence');

  const rafraichir = () => { void horaires.refetch(); void feries.refetch(); void employes.refetch(); };

  return (
    <>
      <Topbar
        title="Pointage"
        subtitle={onglet === 'presence' ? 'Qui est là, qui est en retard, qui est absent' : onglet === 'absences' ? 'Absences justifiées' : onglet === 'etat' ? 'Le mois de chaque employé, validé par le DAF' : 'Horaires, jours fériés, bornes et numéros de pointage'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={rafraichir} aria-label="Rafraîchir">
            <RefreshCw size={14} />
          </button>
        }
      />

      <div className="ds-content" style={{ display: 'grid', gap: 16 }}>
        <div className="ds-rp-tabs" style={{ maxWidth: 640 }}>
          <button className={`ds-rp-tab${onglet === 'presence' ? ' on' : ''}`} onClick={() => setOnglet('presence')}>Présence du jour</button>
          <button className={`ds-rp-tab${onglet === 'absences' ? ' on' : ''}`} onClick={() => setOnglet('absences')}>Absences</button>
          <button className={`ds-rp-tab${onglet === 'etat' ? ' on' : ''}`} onClick={() => setOnglet('etat')}>État mensuel</button>
          <button className={`ds-rp-tab${onglet === 'parametres' ? ' on' : ''}`} onClick={() => setOnglet('parametres')}>Paramètres</button>
        </div>
        {onglet === 'presence' && <PresenceJour />}
        {onglet === 'absences' && <AbsencesPointage />}
        {onglet === 'etat' && <EtatMensuelPointage />}
        {onglet === 'parametres' && (<>
        {/* ── Horaires ─────────────────────────────────────────── */}
        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title"><Clock size={14} style={{ verticalAlign: -2 }} /> Horaires de travail</div>
              <div className="ds-table-sub">Un nouvel horaire s'applique à partir de sa date : les jours d'avant gardent l'ancien.</div>
            </div>
            {peutHoraire && (
              <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setFormHoraire(true)}>
                <Plus size={14} /> Nouvel horaire
              </button>
            )}
          </div>
          <div style={{ padding: 16 }}>
            {horaires.isLoading ? 'Chargement…' : !enVigueur ? (
              <p style={{ color: 'var(--tx-3)', fontSize: 13 }}>Aucun horaire en vigueur.</p>
            ) : (
              <CarteHoraire h={enVigueur} />
            )}
            {aVenir.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--tx-2)' }}>
                À venir : {aVenir.map((h) => `${h.heureDebut}–${h.heureFin} à partir du ${dateFr(h.valableDu)}`).join(' · ')}
              </div>
            )}
            {(horaires.data?.length ?? 0) > 1 && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, cursor: 'pointer' }}>Historique des horaires</summary>
                <ul style={{ fontSize: 12, marginTop: 8, paddingLeft: 18 }}>
                  {horaires.data!.map((h) => (
                    <li key={h.id}>
                      À partir du {dateFr(h.valableDu)} : {h.heureDebut}–{h.heureFin}
                      {h.pauseDebut && `, pause ${h.pauseDebut}–${h.pauseFin}`}, {joursLisibles(h.joursTravailles)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>

        {/* ── Règles de paie (P4) ──────────────────────────────── */}
        <ReglesPaiePointage estDaf={peutHoraire} />

        {/* ── Jours fériés ─────────────────────────────────────── */}
        <JoursFeries
          annee={anneeFeries}
          setAnnee={setAnneeFeries}
          liste={feries.data ?? []}
          chargement={feries.isLoading}
          peutSaisir={peutSaisir}
          onChange={() => qc.invalidateQueries({ queryKey: [...CLE, 'feries'] })}
        />

        {/* ── Bornes (P2) ──────────────────────────────────────── */}
        <BornesPointage peutSaisir={peutSaisir} />

        {/* ── Numéros de pointage ──────────────────────────────── */}
        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title"><Hash size={14} style={{ verticalAlign: -2 }} /> Numéro de pointage</div>
              <div className="ds-table-sub">
                Le numéro que chaque employé tapera sur la borne, puis dans la pointeuse. Un numéro par personne.
              </div>
            </div>
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="ds-tbl" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th>Employé</th>
                  <th style={{ width: 160 }}>Service</th>
                  <th style={{ width: 120 }}>Matricule</th>
                  <th style={{ width: 210 }}>N° de pointage</th>
                </tr>
              </thead>
              <tbody>
                {employes.isLoading && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>Chargement…</td></tr>}
                {!employes.isLoading && (employes.data?.length ?? 0) === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun employé actif</td></tr>
                )}
                {employes.data?.map((e) => (
                  <LigneNumero key={e.id} e={e} peutSaisir={peutSaisir}
                    onChange={() => qc.invalidateQueries({ queryKey: [...CLE, 'employes'] })} />
                ))}
              </tbody>
            </table>
          </div>
          {employes.data && (
            <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--tx-3)' }}>
              {employes.data.filter((e) => e.numeroPointage == null).length} employé(s) sans numéro sur {employes.data.length}.
            </div>
          )}
        </div>
        </>)}
      </div>

      <FormHoraire
        key={formHoraire ? 'ouvert' : 'ferme'}
        open={formHoraire}
        onClose={() => setFormHoraire(false)}
        modele={enVigueur}
        onSaved={() => { setFormHoraire(false); void qc.invalidateQueries({ queryKey: [...CLE, 'horaires'] }); }}
      />
    </>
  );
};

const CarteHoraire = ({ h }: { h: HorairePointage }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, fontSize: 13 }}>
    <Info titre="Journée" valeur={`${h.heureDebut} – ${h.heureFin}`} />
    <Info titre="Pause" valeur={h.pauseDebut ? `${h.pauseDebut} – ${h.pauseFin}` : 'Selon décision PDG'} />
    <Info titre="Jours travaillés" valeur={joursLisibles(h.joursTravailles)} />
    <Info titre="Tolérance de retard" valeur={`${h.toleranceRetardMin} min`} />
    <Info titre="Retard retenu sur salaire" valeur={h.retardRetenu == null ? 'Selon décision PDG' : h.retardRetenu ? 'Oui' : 'Non, seulement noté'} />
    <Info titre="Heures supplémentaires" valeur={h.hsAutorisationRequise ? 'Sur autorisation du DAF' : 'Sans autorisation'} />
    <Info titre="En vigueur depuis le" valeur={dateFr(h.valableDu)} />
  </div>
);

const Info = ({ titre, valeur }: { titre: string; valeur: string }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{titre}</div>
    <div style={{ fontWeight: 600 }}>{valeur}</div>
  </div>
);

const LigneNumero = ({ e, peutSaisir, onChange }: { e: EmployePointage; peutSaisir: boolean; onChange: () => void }) => {
  const [valeur, setValeur] = useState(e.numeroPointage?.toString() ?? '');
  const modifie = valeur !== (e.numeroPointage?.toString() ?? '');
  const enregistrer = useMutation({
    mutationFn: () => pointageApi.attribuerNumero(e.id, valeur.trim() === '' ? null : Number(valeur)),
    onSuccess: (r) => { toast.success('Numéro enregistré', `${r.fullName} : ${r.numeroPointage ?? 'aucun'}`); onChange(); },
  });
  const valide = valeur.trim() === '' || /^[1-9]\d{0,7}$/.test(valeur.trim());
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{e.fullName}</div>
        <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{e.jobTitle}</div>
      </td>
      <td>{e.department}</td>
      <td><span className="ds-ref-code">{e.matricule}</span></td>
      <td>
        {peutSaisir ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="ds-fi" inputMode="numeric" style={{ width: 90 }} value={valeur}
              onChange={(ev) => setValeur(ev.target.value.replace(/\D/g, ''))} placeholder="—" />
            {modifie && (
              <button className="ds-btn ds-btn-p ds-btn-sm" disabled={!valide || enregistrer.isPending} onClick={() => enregistrer.mutate()}>
                {enregistrer.isPending ? '…' : 'Enregistrer'}
              </button>
            )}
          </div>
        ) : (e.numeroPointage ?? '—')}
      </td>
    </tr>
  );
};

const JoursFeries = ({ annee, setAnnee, liste, chargement, peutSaisir, onChange }: {
  annee: number; setAnnee: (a: number) => void; liste: { id: string; jour: string; libelle: string }[];
  chargement: boolean; peutSaisir: boolean; onChange: () => void;
}) => {
  const [jour, setJour] = useState('');
  const [libelle, setLibelle] = useState('');
  const ajouter = useMutation({
    mutationFn: () => pointageApi.creerJourFerie({ jour, libelle: libelle.trim() }),
    onSuccess: (j) => { toast.success('Jour férié ajouté', `${dateFr(j.jour)} — ${j.libelle}`); setJour(''); setLibelle(''); onChange(); },
  });
  const retirer = useMutation({
    mutationFn: (id: string) => pointageApi.retirerJourFerie(id),
    onSuccess: () => { toast.success('Jour férié retiré'); onChange(); },
  });
  const soumettre = (ev: FormEvent) => { ev.preventDefault(); if (jour && libelle.trim().length >= 2) ajouter.mutate(); };

  return (
    <div className="ds-table-panel">
      <div className="ds-table-header">
        <div>
          <div className="ds-table-title"><CalendarDays size={14} style={{ verticalAlign: -2 }} /> Jours fériés</div>
          <div className="ds-table-sub">À saisir par la RH : les fêtes religieuses sont fixées chaque année par décret. Un jour férié passé ne se retire plus.</div>
        </div>
        <select className="ds-fi" style={{ width: 'auto' }} value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
          {[annee - 1, annee, annee + 1].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div style={{ padding: 16 }}>
        {peutSaisir && (
          <form onSubmit={soumettre} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <input type="date" className="ds-fi" style={{ width: 'auto' }} value={jour} onChange={(e) => setJour(e.target.value)} required />
            <input className="ds-fi" style={{ flex: '1 1 180px' }} placeholder="Libellé (ex. Fête de l'Indépendance)"
              value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={80} required />
            <button className="ds-btn ds-btn-p ds-btn-sm" type="submit" disabled={ajouter.isPending}>
              <Plus size={14} /> Ajouter
            </button>
          </form>
        )}
        {chargement ? 'Chargement…' : liste.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun jour férié saisi pour {annee}.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
            {liste.map((j) => (
              <li key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <span style={{ width: 92, fontWeight: 600 }}>{dateFr(j.jour)}</span>
                <span style={{ flex: 1 }}>{j.libelle}</span>
                {peutSaisir && j.jour >= aujourdhui() && (
                  <button className="ds-btn ds-btn-g ds-btn-sm" aria-label={`Retirer ${j.libelle}`}
                    disabled={retirer.isPending} onClick={() => retirer.mutate(j.id)}>
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const FormHoraire = ({ open, onClose, modele, onSaved }: {
  open: boolean; onClose: () => void; modele: HorairePointage | null; onSaved: () => void;
}) => {
  // Remonté à chaque ouverture (clé) : le formulaire repart de l'horaire en vigueur.
  const [f, setF] = useState<NouvelHoraire>(() => vierge(modele));

  const enregistrer = useMutation({
    mutationFn: () => pointageApi.creerHoraire({
      ...f,
      pauseDebut: f.pauseDebut || undefined,
      pauseFin:   f.pauseFin   || undefined,
    }),
    onSuccess: () => { toast.success('Horaire enregistré', `À partir du ${dateFr(f.valableDu)}`); onSaved(); },
  });
  const basculer = (j: number) => setF((x) => ({
    ...x, joursTravailles: x.joursTravailles.includes(j) ? x.joursTravailles.filter((n) => n !== j) : [...x.joursTravailles, j],
  }));

  return (
    <Dialog open={open} onClose={onClose} title="Nouvel horaire" subtitle="Il s'appliquera à partir de la date choisie ; les jours d'avant gardent l'horaire actuel.">
      <form onSubmit={(e) => { e.preventDefault(); enregistrer.mutate(); }} style={{ display: 'grid', gap: 12 }}>
        <label style={{ fontSize: 12 }}>À partir du
          <input type="date" className="ds-fi" min={aujourdhui()} value={f.valableDu} onChange={(e) => setF({ ...f, valableDu: e.target.value })} required />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label style={{ fontSize: 12 }}>Début<input type="time" className="ds-fi" value={f.heureDebut} onChange={(e) => setF({ ...f, heureDebut: e.target.value })} required /></label>
          <label style={{ fontSize: 12 }}>Fin<input type="time" className="ds-fi" value={f.heureFin} onChange={(e) => setF({ ...f, heureFin: e.target.value })} required /></label>
          <label style={{ fontSize: 12 }}>Début de pause<input type="time" className="ds-fi" value={f.pauseDebut ?? ''} onChange={(e) => setF({ ...f, pauseDebut: e.target.value })} /></label>
          <label style={{ fontSize: 12 }}>Fin de pause<input type="time" className="ds-fi" value={f.pauseFin ?? ''} onChange={(e) => setF({ ...f, pauseFin: e.target.value })} /></label>
        </div>
        <div style={{ fontSize: 12 }}>Jours travaillés
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {JOURS.map((nom, i) => (
              <label key={nom} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={f.joursTravailles.includes(i + 1)} onChange={() => basculer(i + 1)} /> {nom}
              </label>
            ))}
          </div>
        </div>
        <label style={{ fontSize: 12 }}>Tolérance de retard (minutes)
          <input type="number" min={0} max={240} className="ds-fi" value={f.toleranceRetardMin}
            onChange={(e) => setF({ ...f, toleranceRetardMin: Number(e.target.value) })} required />
        </label>
        <label style={{ fontSize: 12 }}>Retard retenu sur salaire
          <select className="ds-fi" value={f.retardRetenu == null ? '' : String(f.retardRetenu)}
            onChange={(e) => setF({ ...f, retardRetenu: e.target.value === '' ? null : e.target.value === 'true' })}>
            <option value="">Selon décision PDG (non tranché)</option>
            <option value="true">Oui</option>
            <option value="false">Non, seulement noté</option>
          </select>
        </label>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={f.hsAutorisationRequise} onChange={(e) => setF({ ...f, hsAutorisationRequise: e.target.checked })} />
          Heures supplémentaires payées seulement si autorisées par le DAF
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={enregistrer.isPending || f.joursTravailles.length === 0}>
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

function vierge(m: HorairePointage | null): NouvelHoraire {
  return {
    valableDu:             aujourdhui(),
    heureDebut:            m?.heureDebut ?? '',
    heureFin:              m?.heureFin ?? '',
    pauseDebut:            m?.pauseDebut ?? '',
    pauseFin:              m?.pauseFin ?? '',
    joursTravailles:       m?.joursTravailles ?? [],
    toleranceRetardMin:    m?.toleranceRetardMin ?? 0,
    retardRetenu:          m?.retardRetenu ?? null,
    hsAutorisationRequise: m?.hsAutorisationRequise ?? true,
  };
}
